import { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Maximize2, X, Download, Volume2, VolumeX, Loader2 } from "lucide-react";
import { useReadAloud } from "@/components/SpeakButton";
import { getNarration, playUrl, stopSpeaking, beginSpeech, speechState, onSpeechState } from "@/lib/speech";

export const SlideCarousel = ({ images = [], title = "Teaching slides", deckUrl }) => {
  const [i, setI] = useState(0);
  const [full, setFull] = useState(false);
  const total = images.length;
  const readAloud = useReadAloud();
  const [st, setSt] = useState(speechState());
  const [narrating, setNarrating] = useState(false);
  useEffect(() => onSpeechState((s) => { setSt(s); if (s === "idle") setNarrating(false); }), []);

  const go = useCallback((d) => { stopSpeaking(); setI((p) => Math.min(total - 1, Math.max(0, p + d))); }, [total]);

  // Once a student uses narration on this deck, warm the next slide so the button responds instantly.
  const [engaged, setEngaged] = useState(false);
  useEffect(() => {
    if (!readAloud || !engaged || i + 1 >= total) return;
    getNarration(images[i + 1], title, i + 1).catch(() => {});
  }, [i, images, title, total, readAloud, engaged]);
  useEffect(() => () => stopSpeaking(), []);

  const narrate = async () => {
    if (narrating && (st === "speaking" || st === "loading")) { stopSpeaking(); setNarrating(false); return; }
    const myGen = beginSpeech();
    setNarrating(true);
    setEngaged(true);
    try {
      const n = await getNarration(images[i], title, i);
      await playUrl(n.url, n.text, myGen);
    } catch { setNarrating(false); }
  };

  useEffect(() => { setI(0); }, [images]);
  useEffect(() => {
    // Preload neighbouring slides so navigation feels instant.
    [i + 1, i - 1].forEach((n) => {
      if (n >= 0 && n < total) { const img = new Image(); img.src = images[n]; }
    });
  }, [i, images, total]);
  useEffect(() => {
    if (!full) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "Escape") setFull(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [full, go]);

  if (!total) return null;

  const Stage = ({ big }) => (
    <div className={`relative w-full ${big ? "h-full" : "aspect-video"} bg-black rounded-lg overflow-hidden select-none`} data-testid="slide-carousel-stage">
      <img src={images[i]} alt={`${title} — slide ${i + 1}`} className="w-full h-full object-contain" draggable={false} data-testid="slide-carousel-image" />
      {i > 0 && (
        <button data-testid="slide-prev-btn" onClick={() => go(-1)} aria-label="Previous slide"
          className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur transition-colors">
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      {i < total - 1 && (
        <button data-testid="slide-next-btn" onClick={() => go(1)} aria-label="Next slide"
          className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur transition-colors">
          <ChevronRight className="w-5 h-5" />
        </button>
      )}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-2 px-3 py-1 rounded-full bg-black/50 backdrop-blur">
        <span className="font-mono-data text-xs text-white" data-testid="slide-counter">{i + 1} / {total}</span>
      </div>
      <div className="absolute top-2 right-2 flex items-center gap-1.5">
        {readAloud && (
          <button data-testid="slide-narrate-btn" onClick={narrate} title={narrating && st === "speaking" ? "Stop narration" : "Hear this slide explained"}
            className={`h-8 px-3 rounded-full text-white text-xs flex items-center gap-1.5 backdrop-blur transition-colors ${narrating && st === "speaking" ? "bg-[#E11D48]/80 hover:bg-[#E11D48]" : "bg-[#22D3EE]/80 hover:bg-[#22D3EE] text-[#04121f]"}`}>
            {narrating && st === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : narrating && st === "speaking" ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            {narrating && st === "speaking" ? "Stop" : "Explain"}
          </button>
        )}
        {deckUrl && (
          <a href={deckUrl} download data-testid="slide-download-btn" title="Download deck (.pptx)"
            className="w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur transition-colors">
            <Download className="w-4 h-4" />
          </a>
        )}
        <button data-testid={big ? "slide-exit-fullscreen-btn" : "slide-fullscreen-btn"} onClick={() => setFull(!big)} title={big ? "Exit" : "Fullscreen"}
          className="w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur transition-colors">
          {big ? <X className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Stage big={false} />
      {full && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 sm:p-10" data-testid="slide-fullscreen-overlay" onClick={(e) => { if (e.target === e.currentTarget) setFull(false); }}>
          <div className="w-full max-w-6xl aspect-video"><Stage big /></div>
        </div>
      )}
    </>
  );
};
